import React from "react";
import ReactDOM from "react-dom";
import { Settings2, Trash2 } from "lucide-react";

interface ContextMenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  className?: string;
  show?: boolean;
}

interface NodeContextMenuProps {
  show: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  items: ContextMenuItem[];
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  show,
  position,
  onClose,
  items,
}) => {
  if (!show) return null;

  // Filter out items that should not be shown
  const visibleItems = items.filter((item) => item.show !== false);

  const menuContent = (
    <div
      className="fixed z-[9999] bg-background border shadow-lg rounded-md py-1 px-2 flex items-center gap-2"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: "translate(-50%, calc(-100% - 8px))",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {visibleItems.map((item, index) => (
        <div
          key={index}
          className={`p-1.5 rounded hover:bg-muted cursor-pointer transition-colors ${item.className || ""}`}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          title={item.label}
        >
          {item.icon}
        </div>
      ))}
    </div>
  );

  // Render context menu in a portal to avoid React Flow transform issues
  return ReactDOM.createPortal(menuContent, document.body);
};

export default NodeContextMenu;

// Global state to track the currently open context menu
let globalActiveNodeId: string | null = null;
let globalCloseCallback: (() => void) | null = null;

/**
 * Hook to manage context menu state and behavior
 * Ensures only one context menu is open at a time across all nodes
 */
export const useNodeContextMenu = (nodeId: string) => {
  const [showContextMenu, setShowContextMenu] = React.useState(false);
  const [contextMenuPosition, setContextMenuPosition] = React.useState({
    x: 0,
    y: 0,
  });

  const closeContextMenu = React.useCallback(() => {
    setShowContextMenu(false);
    if (globalActiveNodeId === nodeId) {
      globalActiveNodeId = null;
      globalCloseCallback = null;
    }
  }, [nodeId]);

  const handleContextMenu = React.useCallback(
    (e: React.MouseEvent) => {
      // Disable custom node context menu while a node is in fullscreen mode.
      // Fullscreen state is managed globally on <body> by FullscreenWrapper.
      if (document.body.getAttribute("data-node-fullscreen") === "true") {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Close any previously open context menu
      if (globalCloseCallback && globalActiveNodeId !== nodeId) {
        globalCloseCallback();
      }

      // Get the node element's bounding rectangle in viewport coordinates
      const nodeElement = e.currentTarget as HTMLElement;
      const rect = nodeElement.getBoundingClientRect();

      // Position menu at the center-top of the node
      // getBoundingClientRect() already accounts for zoom/pan transformations
      const menuX = rect.left + rect.width / 2;
      const menuY = rect.top;

      setContextMenuPosition({ x: menuX, y: menuY });
      setShowContextMenu(true);

      // Register this node as the active one
      globalActiveNodeId = nodeId;
      globalCloseCallback = closeContextMenu;
    },
    [nodeId, closeContextMenu],
  );

  // Close context menu on click outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (showContextMenu && globalActiveNodeId === nodeId) {
        closeContextMenu();
      }
    };

    if (showContextMenu) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("contextmenu", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("contextmenu", handleClickOutside);
    };
  }, [showContextMenu, nodeId, closeContextMenu]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (globalActiveNodeId === nodeId) {
        globalActiveNodeId = null;
        globalCloseCallback = null;
      }
    };
  }, [nodeId]);

  return {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  };
};

/**
 * Helper to create common context menu items
 */
export const createContextMenuItems = {
  expandCollapse: (isExpanded: boolean, onToggle: () => void) => ({
    icon: <Settings2 size={15} />,
    label: `${isExpanded ? "Collapse" : "Expand"} Node`,
    onClick: onToggle,
  }),

  delete: (onDelete: () => void, show: boolean = true) => ({
    icon: <Trash2 size={15} />,
    label: "Delete Node",
    onClick: onDelete,
    className: "text-primary",
    show,
  }),
};
